import {
  Component,
  DestroyRef,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { prefersReducedMotion } from '../../../../core/utils/motion.util';
import { JOURNEY_PATHS } from '../../data/landing-content';
import { nudgeForStep } from '../../data/path-card.util';
import { PathCardComponent } from '../path-card/path-card.component';

const CARD_LOOP_MS = 3200;
const SWAP_MS = 450;
const NUDGE_FADE_MS = 350;

/**
 * Auto-cycling path card — timing and sequencing match Alternative D:
 * steps/progress update immediately; nudge fades out, swaps text, fades in.
 */
@Component({
  selector: 'app-path-card-stage',
  standalone: true,
  imports: [PathCardComponent],
  template: `
    <div class="card-stage">
      <app-path-card
        [path]="activePath()"
        [activeStepIndex]="activeStepIndex()"
        [nudge]="displayedNudge()"
        [swapping]="swapping()"
        [nudgeVisible]="nudgeVisible()"
      />
    </div>
  `,
  styleUrl: './path-card-stage.component.scss',
})
export class PathCardStageComponent {
  readonly paths = JOURNEY_PATHS;

  readonly pathIndex = signal(0);
  readonly activeStepIndex = signal(2);
  readonly swapping = signal(false);
  readonly nudgeVisible = signal(true);
  readonly displayedNudge = signal(nudgeForStep(2, JOURNEY_PATHS[0].nudges));

  readonly activePath = computed(() => this.paths[this.pathIndex()]);

  private readonly destroyRef = inject(DestroyRef);
  private loopTimer: ReturnType<typeof setInterval> | null = null;
  private pendingTimers: ReturnType<typeof setTimeout>[] = [];

  constructor() {
    afterNextRender(() => {
      if (!prefersReducedMotion()) {
        this.startLoop();
      }
      this.destroyRef.onDestroy(() => {
        this.stopLoop();
        this.clearPendingTimers();
      });
    });
  }

  /** Called from hero when a chip selects a matching journey. */
  swapTo(pathIndex: number, startStep = 0): void {
    if (
      pathIndex === this.pathIndex() &&
      startStep === this.activeStepIndex()
    ) {
      return;
    }

    this.clearPendingTimers();
    this.swapping.set(true);
    this.nudgeVisible.set(false);

    this.schedule(() => {
      this.pathIndex.set(pathIndex);
      this.activeStepIndex.set(startStep);
      this.displayedNudge.set(
        nudgeForStep(startStep, this.paths[pathIndex].nudges),
      );
      this.swapping.set(false);
      this.nudgeVisible.set(true);
    }, SWAP_MS);
  }

  private startLoop(): void {
    if (this.loopTimer) return;

    this.loopTimer = setInterval(() => {
      const path = this.paths[this.pathIndex()];
      if (this.activeStepIndex() >= path.steps.length) {
        this.swapTo((this.pathIndex() + 1) % this.paths.length, 0);
        return;
      }
      this.advanceStep();
    }, CARD_LOOP_MS);
  }

  /** Steps animate immediately; nudge cross-fades like the mockup. */
  private advanceStep(): void {
    const pathIdx = this.pathIndex();
    const nextStep = this.activeStepIndex() + 1;
    const path = this.paths[pathIdx];

    this.activeStepIndex.set(nextStep);
    this.nudgeVisible.set(false);

    this.schedule(() => {
      this.displayedNudge.set(nudgeForStep(nextStep, path.nudges));
      this.nudgeVisible.set(true);
    }, NUDGE_FADE_MS);
  }

  private stopLoop(): void {
    if (this.loopTimer) {
      clearInterval(this.loopTimer);
      this.loopTimer = null;
    }
  }

  private schedule(fn: () => void, ms: number): void {
    const id = setTimeout(fn, ms);
    this.pendingTimers.push(id);
  }

  private clearPendingTimers(): void {
    this.pendingTimers.forEach(clearTimeout);
    this.pendingTimers = [];
  }
}
